import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Modal,
  Tag,
  InlineLoading,
  InlineNotification,
  Button,
} from '@carbon/react';
import { Send, Time, CheckmarkFilled, Edit, View } from '@carbon/icons-react';
import { versionLabel } from '@snxt/snxt-onto-share';
import { useVersions } from '../../lib/query/hooks/useVersions';

interface VersionHistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  ontologyId: string | undefined;
  /** When set, renders a "Publish current draft" CTA that opens the publish dialog. */
  onPublishClick?: () => void;
  /** Whether the current viewer can publish (owner only). */
  canPublish?: boolean;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * VersionHistoryPanel
 *
 * Step C — read-only timeline of every published version of the ontology
 * plus the current draft. Mounts as a Carbon Modal (passive, centered) so
 * users can review what's been shipped and what's still in flight without
 * leaving the editor.
 *
 * Why a Modal and not a side panel: the editor canvas already pins a
 * left-side inspector and a right-side AI panel; introducing a third
 * docked panel would crowd the viewport on standard laptop screens.
 * A modal keeps the timeline focused and dismissible.
 */
export const VersionHistoryPanel = ({
  isOpen,
  onClose,
  ontologyId,
  onPublishClick,
  canPublish = false,
}: VersionHistoryPanelProps) => {
  const versionsQuery = useVersions(isOpen ? ontologyId : undefined);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // The version currently being viewed (read-only snapshot mode). We use this
  // to (a) highlight the active row in the timeline and (b) hide the "View
  // snapshot" button on the row that's already loaded.
  const currentlyViewing = searchParams.get('version');

  const goToSnapshot = (versionId: string) => {
    if (!ontologyId) return;
    onClose();
    navigate(`/editor/${ontologyId}?version=${versionId}`);
  };

  const goToDraft = () => {
    if (!ontologyId) return;
    onClose();
    navigate(`/editor/${ontologyId}`);
  };

  // Sort: draft first, then published versions newest → oldest. The API
  // already orders by created_at DESC, but we re-bucket to make sure the
  // single draft is always pinned to the top regardless of when it was
  // created (e.g. legacy ontologies whose draft row was inserted later
  // than some published rows during backfill).
  const { draft, published } = useMemo(() => {
    const list = versionsQuery.data ?? [];
    return {
      draft: list.find((v) => v.status === 'draft') ?? null,
      published: list
        .filter((v) => v.status === 'published')
        .sort(
          (a, b) =>
            new Date(b.published_at ?? b.created_at).getTime() -
            new Date(a.published_at ?? a.created_at).getTime(),
        ),
    };
  }, [versionsQuery.data]);

  // The published number the draft was forked from. Null on an ontology that has
  // never been published, which is a real state, not a missing value.
  const draftParentNumber = useMemo(() => {
    if (!draft?.parent_version_id) return null;
    return (
      published.find((p) => p.id === draft.parent_version_id)?.version_number ?? null
    );
  }, [draft, published]);

  return (
    <Modal
      open={isOpen}
      onRequestClose={onClose}
      modalHeading="Version history"
      modalLabel="Versioning"
      passiveModal
      size="md"
    >
      {versionsQuery.isLoading && (
        <InlineLoading description="Loading version history…" />
      )}

      {versionsQuery.isError && (
        <InlineNotification
          kind="error"
          title="Failed to load versions"
          subtitle={
            versionsQuery.error instanceof Error
              ? versionsQuery.error.message
              : 'Unknown error'
          }
          hideCloseButton
        />
      )}

      {!versionsQuery.isLoading && !versionsQuery.isError && (
        <div className="version-history">
          {/* Draft section */}
          {draft && (
            <div className="version-history__section">
              <h5 className="version-history__section-title">Working copy</h5>
              <div className="version-history__item version-history__item--draft">
                <div className="version-history__item-icon">
                  <Edit size={20} />
                </div>
                <div className="version-history__item-body">
                  <div className="version-history__item-header">
                    {/* A draft has no number; it is named by what it is based on. */}
                    <span
                      className="version-history__version-number"
                      data-testid="version-history-draft-label"
                    >
                      {versionLabel(draft, draftParentNumber)}
                    </span>
                    <Tag type="cyan" size="sm">
                      Draft
                    </Tag>
                    {!currentlyViewing && (
                      <Tag type="purple" size="sm">
                        Viewing
                      </Tag>
                    )}
                  </div>
                  <div className="version-history__meta">
                    <Time size={14} />
                    <span>Created {formatDate(draft.created_at)}</span>
                  </div>
                  {draftParentNumber && (
                    <div className="version-history__meta">
                      Forked from v{draftParentNumber}
                    </div>
                  )}
                  <div
                    style={{
                      marginTop: '0.75rem',
                      display: 'flex',
                      gap: '0.5rem',
                      flexWrap: 'wrap',
                    }}
                  >
                    {currentlyViewing && (
                      <Button
                        kind="tertiary"
                        size="sm"
                        renderIcon={Edit}
                        onClick={goToDraft}
                      >
                        Open draft
                      </Button>
                    )}
                    {canPublish && onPublishClick && !currentlyViewing && (
                      <Button
                        kind="primary"
                        size="sm"
                        renderIcon={Send}
                        onClick={() => {
                          onClose();
                          onPublishClick();
                        }}
                      >
                        Publish this draft
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Published timeline */}
          <div className="version-history__section">
            <h5 className="version-history__section-title">
              Published versions
              {published.length > 0 && (
                <span className="version-history__count">
                  {' '}
                  · {published.length}
                </span>
              )}
            </h5>

            {published.length === 0 && (
              <p className="version-history__empty">
                No versions have been published yet. Publishing the current
                draft snapshots it as an immutable version.
              </p>
            )}

            {published.length > 0 && (
              <ol className="version-history__timeline">
                {published.map((v, idx) => {
                  const isActive = currentlyViewing === v.id;
                  return (
                    <li
                      key={v.id}
                      className={`version-history__item version-history__item--published${
                        idx === 0 ? ' version-history__item--latest' : ''
                      }${isActive ? ' version-history__item--active' : ''}`}
                    >
                      <div className="version-history__item-icon">
                        <CheckmarkFilled size={20} />
                      </div>
                      <div className="version-history__item-body">
                        <div className="version-history__item-header">
                          <span className="version-history__version-number">
                            {versionLabel(v)}
                          </span>
                          <Tag type="green" size="sm">
                            Published
                          </Tag>
                          {idx === 0 && (
                            <Tag type="blue" size="sm">
                              Latest
                            </Tag>
                          )}
                          {isActive && (
                            <Tag type="purple" size="sm">
                              Viewing
                            </Tag>
                          )}
                        </div>
                        <div className="version-history__meta">
                          <Time size={14} />
                          <span>
                            Published {formatDate(v.published_at)}
                          </span>
                        </div>
                        {v.release_notes && (
                          <p className="version-history__notes">
                            {v.release_notes}
                          </p>
                        )}
                        {!isActive && (
                          <div style={{ marginTop: '0.5rem' }}>
                            <Button
                              kind="tertiary"
                              size="sm"
                              renderIcon={View}
                              onClick={() => goToSnapshot(v.id)}
                            >
                              View snapshot
                            </Button>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

// Made with Bob
